import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Trash, Warning, ShieldCheck } from "@phosphor-icons/react";

/**
 * MaintenanceTab — danger-zone UI for bulk-clearing collections.
 * Backed by POST /admin/maintenance/clear; restricted to the Original Admin
 * server-side, but we also hide the panel for any other role on the
 * client.
 *
 * The operator must:
 *   1. tick at least one scope checkbox
 *   2. type CLEAR into the confirmation box
 * before the destructive button enables.
 */
const SCOPE_LABELS = {
  supply_requests: "Supply Requests",
  supervisor_hub:  "Supervisor Hub (write-ups, tour reports, incident reports)",
  tours:           "Schedule / Tours",
  documents:       "Personnel Documents",
  notifications:   "Notifications",
  inventory:       "Inventory",
  validation:      "Validation Records",
};

export default function MaintenanceTab() {
  const { user } = useAuth();
  // Maintenance is unlocked for the Original Admin plus the no-restrictions
  // top-tier roles (Commanding Officer and Super Admin).
  const extra = Array.isArray(user?.additional_roles) ? user.additional_roles : [];
  const owned = new Set([user?.role, ...extra]);
  const allowed = !!user?.is_original_admin || owned.has("commanding_officer") || owned.has("super_admin");
  const [scopes, setScopes] = useState([]);
  const [picked, setPicked] = useState({});
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  async function load() {
    if (!allowed) return;
    try {
      const { data } = await api.get("/admin/maintenance/scopes");
      setScopes(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load maintenance scopes");
    }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [allowed]);

  function toggle(scope) {
    setPicked((p) => ({ ...p, [scope]: !p[scope] }));
  }

  const selected = Object.keys(picked).filter((k) => picked[k]);
  const canExecute = selected.length > 0 && confirm === "CLEAR";

  async function execute() {
    if (!canExecute) return;
    const totalRows = scopes
      .filter((s) => selected.includes(s.scope))
      .reduce((acc, s) => acc + s.total, 0);
    if (!window.confirm(`Permanently delete ${totalRows} document(s) across ${selected.length} scope(s)? This cannot be undone.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/maintenance/clear", { scopes: selected, confirm: "CLEAR" });
      const summary = Object.entries(data.result)
        .map(([scope, byColl]) => {
          const n = Object.values(byColl).reduce((a, b) => a + b, 0);
          return `${scope}: ${n}`;
        })
        .join(" · ");
      toast.success(`Cleared. ${summary}`);
      setPicked({}); setConfirm("");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Clear failed");
    } finally { setBusy(false); }
  }

  if (!allowed) {
    return (
      <div className="surface-grid p-6 max-w-2xl">
        <div className="flex items-center gap-2 mb-2">
          <ShieldCheck size={18} weight="bold" className="text-slate-500" />
          <h3 className="font-display font-bold">Maintenance</h3>
        </div>
        <p className="text-sm text-slate-600">Restricted to the Original Admin account.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4 max-w-3xl" data-testid="maintenance-tab">
      <div className="border border-red-300 bg-red-50 rounded-sm p-4 flex items-start gap-3">
        <Warning size={20} weight="bold" className="text-red-700 mt-0.5" />
        <div className="text-xs text-red-900 leading-relaxed">
          <strong className="block mb-1">Danger Zone — irreversible bulk delete.</strong>
          Selected collections will be wiped from the live database. There is no undo. Use this to clear stale test data from production after deploy.
        </div>
      </div>

      <div className="surface-grid">
        <div className="px-4 py-2 border-b border-slate-200 bg-slate-50">
          <div className="font-display font-bold text-sm">Choose collections to clear</div>
        </div>
        <div className="divide-y divide-slate-100">
          {scopes.map((s) => (
            <label
              key={s.scope}
              className={`flex items-start gap-3 px-4 py-3 cursor-pointer hover:bg-slate-50 ${picked[s.scope] ? "bg-red-50" : ""}`}
              data-testid={`maint-row-${s.scope}`}
            >
              <Checkbox checked={!!picked[s.scope]} onCheckedChange={() => toggle(s.scope)} className="mt-0.5" />
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm">{SCOPE_LABELS[s.scope] || s.scope}</div>
                <div className="text-[11px] text-slate-500 mono mt-0.5">
                  {s.collections.map((c) => `${c} (${s.counts[c]})`).join(" · ")}
                </div>
              </div>
              <span className={`tab-tag whitespace-nowrap ${s.total === 0 ? "text-slate-400" : "text-red-700"}`}>
                {s.total} row{s.total === 1 ? "" : "s"}
              </span>
            </label>
          ))}
        </div>
      </div>

      <div className="surface-grid p-4 space-y-3">
        <Label className="tab-tag">Type <span className="mono font-bold">CLEAR</span> to enable</Label>
        <Input
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          placeholder='Type CLEAR'
          className="rounded-sm mono"
          data-testid="maint-confirm"
        />
        <div className="flex items-center justify-between pt-2 border-t border-slate-200">
          <p className="text-[11px] text-slate-500">
            {selected.length === 0
              ? "Select at least one collection."
              : `Will delete ${selected.length} scope${selected.length === 1 ? "" : "s"}.`}
          </p>
          <Button
            onClick={execute}
            disabled={!canExecute || busy}
            className="rounded-sm bg-red-700 hover:bg-red-800 text-white"
            data-testid="maint-execute"
          >
            <Trash size={14} weight="bold" className="mr-1" />
            {busy ? "Clearing…" : "Permanently Clear"}
          </Button>
        </div>
      </div>
    </div>
  );
}
